import { createTheme } from "@mui/material/styles";
import type { CSSObject, Theme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#7305BD",
      light: "#9A32D4",
      dark: "#5A0496",
    },
    secondary: {
      main: "#3A7DFF",
    },
    background: {
      default: "linear-gradient(79deg, #6B35AD 0%, #28083B 100%)",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1E1E1E",
      secondary: "#5C5C5C",
      disabled: "#9E9E9E",
    },
    divider: "#E0E0E0",
    warning: {
      main: "#FFB020",
    },
  },
  typography: {
    fontFamily: "'Nunito', sans-serif",
    h1: {
      fontSize: "2rem", // 32px
      fontWeight: 700,
      fontFamily: "'Nunito', sans-serif",
    },
    h2: {
      fontSize: "1.5rem", // 24px
      fontWeight: 600,
      fontFamily: "'Nunito', sans-serif",
    },
    h3: {
      fontFamily: "'Nunito', sans-serif",
    },
    h4: {
      fontFamily: "'Nunito', sans-serif",
    },
    h5: {
      fontFamily: "'Nunito', sans-serif",
    },
    h6: {
      fontFamily: "'Nunito', sans-serif",
    },
    body1: {
      fontSize: "1rem", // 16px
      fontWeight: 400,
      fontFamily: "'Nunito', sans-serif",
    },
    body2: {
      fontFamily: "'Nunito', sans-serif",
    },
    button: {
      fontSize: "0.875rem", // 14px
      fontWeight: 600,
      textTransform: "none",
      fontFamily: "'Nunito', sans-serif",
    },
    caption: {
      fontSize: "0.75rem", // 12px
      fontWeight: 500,
      fontFamily: "'Nunito', sans-serif",
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(0,0,0,0.08)",
    "0px 4px 8px rgba(0,0,0,0.12)",
    "0px 6px 12px rgba(0,0,0,0.12)",
    "0px 8px 16px rgba(0,0,0,0.12)",
    "0px 10px 20px rgba(0,0,0,0.12)",
    "0px 12px 24px rgba(0,0,0,0.16)",
    "0px 14px 28px rgba(0,0,0,0.16)",
    "0px 16px 32px rgba(0,0,0,0.16)",
    "0px 18px 36px rgba(0,0,0,0.20)",
    "0px 20px 40px rgba(0,0,0,0.20)",
    "0px 22px 44px rgba(0,0,0,0.20)",
    "0px 24px 48px rgba(0,0,0,0.24)",
    "0px 26px 52px rgba(0,0,0,0.24)",
    "0px 28px 56px rgba(0,0,0,0.24)",
    "0px 30px 60px rgba(0,0,0,0.28)",
    "0px 32px 64px rgba(0,0,0,0.28)",
    "0px 34px 68px rgba(0,0,0,0.28)",
    "0px 36px 72px rgba(0,0,0,0.32)",
    "0px 38px 76px rgba(0,0,0,0.32)",
    "0px 40px 80px rgba(0,0,0,0.32)",
    "0px 42px 84px rgba(0,0,0,0.36)",
    "0px 44px 88px rgba(0,0,0,0.36)",
    "0px 46px 92px rgba(0,0,0,0.36)",
    "0px 48px 96px rgba(0,0,0,0.40)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "8px 20px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 4px 8px rgba(0,0,0,0.12)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// ----------------------------------------------------------------------

/**
 * Usage:
 * ...hideScrollX,
 * ...hideScrollY,
 */
export const hideScrollX: CSSObject = {
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  overflowX: "auto",
  "&::-webkit-scrollbar": { display: "none" },
};

export const hideScrollY: CSSObject = {
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  overflowY: "auto",
  "&::-webkit-scrollbar": { display: "none" },
};

/**
 * Usage:
 * ...textGradient(`to right, ${theme.vars.palette.text.primary}, ${alpha(theme.vars.palette.text.primary, 0.2)}`
 */
export function textGradient(color: string): CSSObject {
  return {
    background: `linear-gradient(${color})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    textFillColor: "transparent",
    color: "transparent",
  };
}

/**
 * Usage:
 * ...bgGradient({ color: `to right, ${theme.vars.palette.grey[900]} 25%, ${varAlpha(theme.vars.palette.primary.darkerChannel, 0.88)}`, imgUrl: '/assets/background/overlay.png' }),
 */
export type BgGradientProps = {
  color: string;
  imgUrl?: string;
};

export function bgGradient({ color, imgUrl }: BgGradientProps): CSSObject {
  if (imgUrl) {
    return {
      background: `linear-gradient(${color}), url(${imgUrl})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
    };
  }
  return { background: `linear-gradient(${color})` };
}

/**
 * Usage:
 * ...bgBlur({ color: `varAlpha(theme.vars.palette.background.paperChannel, 0.8)`, imgUrl: '/assets/background/overlay.png', blur: 6 }),
 */
export type BgBlurProps = {
  color: string;
  blur?: number;
  imgUrl?: string;
};

export function bgBlur({ color, blur = 6, imgUrl }: BgBlurProps): CSSObject {
  if (imgUrl) {
    return {
      position: "relative",
      backgroundImage: `url(${imgUrl})`,
      "&::before": {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 9,
        content: '""',
        width: "100%",
        height: "100%",
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        backgroundColor: color,
      },
    };
  }
  return {
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    backgroundColor: color,
  };
}

// ----------------------------------------------------------------------

export const stylesMode = {
  light: '[data-mui-color-scheme="light"] &',
  dark: '[data-mui-color-scheme="dark"] &',
};

export const mediaQueries = {
  upXs: "@media (min-width:0px)",
  upSm: "@media (min-width:600px)",
  upMd: "@media (min-width:900px)",
  upLg: "@media (min-width:1200px)",
  upXl: "@media (min-width:1536px)",
};

/**
 * Set font family
 */
export function setFont(fontName: string) {
  return `"${fontName}", sans-serif`;
}

/**
 * Converts rem to px
 */
export function remToPx(value: string): number {
  return Math.round(parseFloat(value) * 16);
}

/**
 * Converts px to rem
 */
export function pxToRem(value: number): string {
  return `${value / 16}rem`;
}

/**
 * Responsive font sizes
 */
export function responsiveFontSizes({
  sm,
  md,
  lg,
}: {
  sm: number;
  md: number;
  lg: number;
}) {
  return {
    [mediaQueries.upSm]: { fontSize: pxToRem(sm) },
    [mediaQueries.upMd]: { fontSize: pxToRem(md) },
    [mediaQueries.upLg]: { fontSize: pxToRem(lg) },
  };
}

/**
 * Converts a hex color to RGB channels
 */
export function hexToRgbChannel(hex: string) {
  if (!/^#[0-9A-F]{6}$/i.test(hex)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);

  return `${r} ${g} ${b}`;
}

/**
 * Converts a hex color to RGB channels
 */
export function createPaletteChannel(hexPalette: Record<string, string>) {
  const channelPalette: Record<string, string> = {};

  Object.entries(hexPalette).forEach(([key, value]) => {
    channelPalette[`${key}Channel`] = hexToRgbChannel(value);
  });

  return { ...hexPalette, ...channelPalette };
}

/**
 * Color with alpha channel
 */
export function varAlpha(color: string, opacity = 1) {
  const unsupported =
    color.startsWith("#") ||
    color.startsWith("rgb") ||
    color.startsWith("rgba") ||
    (!color.includes("var") && color.includes("Channel"));

  if (unsupported) {
    color = hexToRgbChannel(color);
  }

  return `rgba(${color} / ${opacity})`;
}

export const baseVars = (theme: Theme) => ({
  // nav
  "--layout-nav-bg": "transparent",
  "--layout-nav-border-color": varAlpha(theme.palette.grey[500], 0.08),
  "--layout-nav-zIndex": 1101,
  "--layout-nav-mobile-width": "320px",
  // nav item
  "--layout-nav-item-height": "44px",
  "--layout-nav-item-color": "white",
  "--layout-nav-item-active-color": "white",
  "--layout-nav-item-active-bg": "transparent",
  "--layout-nav-item-hover-bg": varAlpha("#ffffff", 0.08),
  // header
  "--layout-header-zIndex": 1100,
  "--layout-header-mobile-height": "64px",
  "--layout-header-desktop-height": "64px",
});

export default theme;
